import { Mic, X, Send } from "lucide-react";
import { useMediaRecorder } from "@/hooks/useMediaRecorder";
import { motion, AnimatePresence } from "framer-motion";

interface VoiceNoteRecorderProps {
  onSend: (url: string, duration: number) => void;
  onCancel?: () => void;
}

export function VoiceNoteRecorder({ onSend, onCancel }: VoiceNoteRecorderProps) {
  const { isRecording, duration, startRecording, stopRecording, cancelRecording } = useMediaRecorder();

  const handleStop = async () => {
    const result = await stopRecording();
    if (result) {
      onSend(result.url, result.duration);
    }
  };

  const handleCancel = () => {
    cancelRecording();
    onCancel?.();
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!isRecording) {
    return (
      <button
        onClick={startRecording}
        className="p-2 text-gray-400 hover:text-[#D4AF37] transition-colors"
        title="Record voice note"
      >
        <Mic size={20} />
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center gap-3 flex-1"
    >
      <button onClick={handleCancel} className="text-red-500 hover:text-red-400">
        <X size={18} />
      </button>
      <div className="flex items-center gap-2 flex-1">
        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-xs text-red-400 font-mono">{formatDuration(duration)}</span>
        <div className="flex-1 h-0.5 bg-gray-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-red-500"
            animate={{ width: ["0%", "100%"] }}
            transition={{ duration: 120, ease: "linear" }}
          />
        </div>
      </div>
      <button
        onClick={handleStop}
        className="p-2 bg-[#D4AF37] text-black rounded-full hover:bg-[#B48F1B]"
      >
        <Send size={14} />
      </button>
    </motion.div>
  );
}
